// Request and response contracts for the flight planner (folded in from
// webplan-modern). The pure rules they feed are in navigation.ts,
// fuelPlan.ts, weightBalance.ts, crosswind.ts and icaoFlightPlan.ts.

import { z } from 'zod'
import { DISTANCE_UNITS, SPEED_UNITS } from './navigation'

// A CG envelope vertex: metres aft of the datum, kg.
export const EnvelopePointSchema = z.object({
  arm: z.number().min(-10).max(20),
  kg: z.number().positive().max(20000),
})

// A polygon needs three corners; twenty is far more than any flight manual
// draws.
export const CgEnvelopeSchema = z.array(EnvelopePointSchema).min(3).max(20)

const Arm = z.number().min(-10).max(20)

// A baggage compartment: where its middle is and what it may hold.
export const BaggageAreaSchema = z.object({
  label: z.string().trim().min(1).max(30),
  arm: Arm,
  maxKg: z.number().positive().max(1000),
})
const Usg = z.number().nonnegative().max(1000)
const Speed = z.number().nonnegative().max(1000)
// The flight manual's own wording: "78/105 tel.alh/ylh", "600 m".
const Note = z.string().trim().max(60).nullable().optional()

// flight.aircraft_performance, as the admin fleet editor (and a member,
// for their private aircraft) writes it.
export const AircraftPerformanceSchema = z.object({
  emptyWeightKg: z.number().positive().max(20000),
  maxFuelMainUsg: Usg,
  maxFuelResUsg: Usg,
  unusableFuelUsg: Usg,
  speedUnit: z.enum(SPEED_UNITS),
  distanceUnit: z.enum(DISTANCE_UNITS),
  cruiseSpeed: Speed,
  manualCruiseSpeed: Speed.positive().nullable().optional(),
  stallSpeedFlaps: Note,
  stallSpeedClean: Note,
  approachSpeed: Note,
  bestGlideSpeed: Note,
  bestClimbSpeed: Note,
  steepestClimbSpeed: Note,
  runwayRequirement: Note,
  crosswindLimitKt: z.number().nonnegative().max(99).nullable().optional(),
  // Taxi time before take-off for the worked W&B sheet, minutes.
  taxiMinutes: z.number().min(0).max(60).default(10),
  // Fuel burned per 10 minutes of taxi, litres.
  taxiLitresPerTenMin: z.number().min(0).max(20).default(1),
  hasRearSeats: z.boolean(),
  armBasicMass: Arm,
  armFrontPax: Arm,
  armRearPax: Arm,
  armCargo: Arm,
  // Empty: the single cargo station at armCargo, no limit.
  baggageAreas: z.array(BaggageAreaSchema).max(6).default([]),
  baggageMaxTotalKg: z.number().positive().max(1000).nullable().optional(),
  armFuelMain: Arm,
  armFuelRes: Arm,
  cgEnvelope: CgEnvelopeSchema,
})
export type AircraftPerformanceInput = z.infer<typeof AircraftPerformanceSchema>

export type AircraftPerformance = AircraftPerformanceInput & {
  registration: string
  updatedAt: string
  // The engine's rating from the flight manual; not edited with the rest
  // (no admin form for them yet), so not part of the input.
  engineRatedBhp?: number | null
  engineRatedRpm?: number | null
  engineMaxContinuousRpm?: number | null
}

// --- The current flight plan ---------------------------------------------

const Text = (max: number) => z.string().trim().max(max).nullable().optional()

// One point of the plan, as the member edits it: a SNAPSHOT of the
// waypoint (plan.flight_plan_legs) plus the wind and TAS for the leg into
// it. Positions are carried because a snapshot must survive the waypoint
// being edited or deleted.
export const PlanLegSchema = z.object({
  // The waypoint this snapshot was taken from, while it still exists.
  waypointCode: z.string().trim().min(1).max(20).nullable().optional(),
  code: z.string().trim().min(1).max(20),
  name: Text(100),
  lat: z.number().min(-90).max(90).nullable(),
  lon: z.number().min(-180).max(180).nullable(),
  variation: z.number().min(-180).max(180).nullable().optional(),
  com: Text(40),
  gnd: Text(40),
  atis: Text(40),
  area: Text(100),
  vor: Text(40),
  ndb: Text(40),
  country: z.string().regex(/^[a-z]{2}$/).nullable().optional(),
  link: Text(500),
  windDir: z.number().int().min(0).max(360),
  windSpeedKt: z.number().min(0).max(200),
  tasKt: z.number().int().positive().max(1000).nullable().optional(),
})
export type PlanLegInput = z.infer<typeof PlanLegSchema>

// The whole plan, replaced at once: the editor always holds all of it, and
// a partial update of an ordered list is where plans go wrong.
export const LOCAL_RADII_NM = [5, 10, 15, 20] as const
export const LOCAL_MINUTES = [30, 45, 60] as const

export const SavePlanSchema = z.object({
  // A local flight: stay within this many NM of the first point. null for a cross-country.
  // Its planned time in the air, which is the fuel plan's trip time.
  localMinutes: z.union([z.literal(30), z.literal(45), z.literal(60)]).nullable().default(null),
  localRadiusNm: z.union([z.literal(5), z.literal(10), z.literal(15), z.literal(20)]).nullable().default(null),
  registration: z.string().trim().toUpperCase().nullable(),
  // The common wind every leg starts with.
  windDir: z.number().int().min(0).max(360).default(0),
  windSpeedKt: z.number().min(0).max(200).default(0),
  legs: z.array(PlanLegSchema).max(60),
})
export type SavePlanInput = z.infer<typeof SavePlanSchema>

const Kg = z.number().min(0).max(5000)
export const LoadDataSchema = z.object({
  frontKg: Kg,
  rearKg: Kg,
  cargoKg: Kg,
  // Per baggage area, in the aircraft's order; cargoKg is their total.
  baggageKg: z.array(Kg).max(6).optional(),
  fuelMain: z.number().min(0).max(5000),
  fuelRes: z.number().min(0).max(5000),
  fuelUnit: z.enum(['usg', 'l']),
})
export type LoadDataInput = z.infer<typeof LoadDataSchema>

// null clears the member's own figure for that aircraft.
export const CruiseOverrideSchema = z.object({
  cruiseSpeed: z.number().positive().max(1000).nullable(),
})

export const SaveRouteSchema = z.object({
  name: z.string().trim().min(1).max(100),
})

// --- Waypoints -----------------------------------------------------------

// ad: aerodrome (the flight logs' airfield lookup); vrp: visual reporting
// point; udef: a point a member defined.
export const WAYPOINT_TYPES = ['ad', 'vrp', 'udef'] as const
export type WaypointType = (typeof WAYPOINT_TYPES)[number]

export const WaypointSchema = z.object({
  code: z.string().trim().toUpperCase().min(1).max(20),
  name: Text(100),
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
  variation: z.number().min(-180).max(180).nullable().optional(),
  com: Text(40),
  gnd: Text(40),
  atis: Text(40),
  area: Text(100),
  vor: Text(40),
  country: z.string().regex(/^[a-z]{2}$/),
  // Omitted: a new point is 'udef', a corrected one keeps its type. Only
  // fpl.admin may make or keep an aerodrome.
  waypointType: z.enum(WAYPOINT_TYPES).optional(),
  // Shown as a link to everyone the waypoint is shared with: http(s) only.
  link: z.string().trim().url().regex(/^https?:\/\//i).max(500).nullable().optional(),
})
export type WaypointInput = z.infer<typeof WaypointSchema>

export const ShareWaypointSchema = z.object({ isShared: z.boolean() })
